import React, { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PhoneNumberList from './phone-number-list';
import { RootState } from '../../../../../../store/root-reducer';
import { GetPhoneNumbersListPayload } from '../../../../types/phone-numbers';
import { getMyPhoneNumberListAction, getCountryCodeOptionsListForFiltersRequest, getRegionOptionsListForFiltersRequest, getUsersListRequest } from '../../../../extra-actions';
import { isRequestPending } from '../../../../../settings/components/billing-subscription/utils/helper';
import { setMyPhoneNumbersListFilters } from '../../../../dialer-slice';
import { getAdminSettingsRequest } from '../../../../../settings/components/admin-settings/extra-actions';

interface IProps {

}

const PhoneNumberListContainer: React.FC<IProps> = () => {
  const dispatch = useDispatch();

  // Get data from Redux
  const myPhoneNumbersList = useSelector(
    (state: RootState) => state.dialer.myPhoneNumbersList,
  );
  const getMyPhoneNumberListRequest = useSelector(
    (state: RootState) => state.dialer.getMyPhoneNumberListRequest,
  );
  const myPhoneNumbersListFilters = useSelector(
    (state: RootState) => state.dialer.myPhoneNumbersListFilters,
  );
  const isGetMyPhoneNumbersListRequestPending = isRequestPending(getMyPhoneNumberListRequest.status);

  // Fetch phone numbers from API using Redux
  const fetchPhoneNumbers = useCallback(
    (payload: GetPhoneNumbersListPayload) => {
      dispatch(getMyPhoneNumberListAction(payload));
    },
    [dispatch],
  );

  // Update filters in Redux
  const updateFilters = useCallback(
    (filters: typeof myPhoneNumbersListFilters) => {
      dispatch(setMyPhoneNumbersListFilters(filters));
    },
    [dispatch],
  );

  const sendGetAdminSettingsRequest = useCallback(() => {
    dispatch(getAdminSettingsRequest());
  }, [dispatch]);

  const sendGetCountryCodeOptionsListForFiltersRequest = useCallback(() => {
    dispatch(getCountryCodeOptionsListForFiltersRequest());
  }, [dispatch]);

  const sendGetRegionOptionsListForFiltersRequest = useCallback(() => {
    dispatch(getRegionOptionsListForFiltersRequest());
  }, [dispatch]);

  const sendGetTeamMembersList = useCallback(() => {
    dispatch(getUsersListRequest());
  }, [dispatch]);

  // Get filter options from Redux
  const countriesOptionsForNumbersSearch = useSelector(
    (state: RootState) => state.dialer.countryCodeOptionsListForFilters,
  );
  const teamMembersList = useSelector(
    (state: RootState) => state.dialer.teamMembersList,
  );
  const regionOptionsListForFilters = useSelector(
    (state: RootState) => state.dialer.regionOptionsListForFilters,
  );

  // Get request states for listening to success
  const assignPhoneNumberRequestState = useSelector(
    (state: RootState) => state.dialer.assignPhoneNumberRequest,
  );
  const bulkAssignPhoneNumberRequestState = useSelector(
    (state: RootState) => state.dialer.bulkAssignPhoneNumberRequest,
  );
  const setPhoneNumberLabelRequestStatus = useSelector(
    (state: RootState) => state.dialer.setPhoneNumberLabelRequest,
  );
  const bulkSetPhoneNumberLabelRequestStatus = useSelector(
    (state: RootState) => state.dialer.bulkSetPhoneNumberLabelRequest,
  );
  const deletePhoneNumberRequestStatus = useSelector(
    (state: RootState) => state.dialer.deletePhoneNumberRequest,
  );
  const revokePhoneNumberReleaseRequestStatus = useSelector(
    (state: RootState) => state.dialer.revokePhoneNumberReleaseRequest,
  );

  return (
    <PhoneNumberList
      myPhoneNumbersList={myPhoneNumbersList}
      isGetMyPhoneNumbersListRequestPending={isGetMyPhoneNumbersListRequestPending}
      fetchPhoneNumbers={fetchPhoneNumbers}
      myPhoneNumbersListFilters={myPhoneNumbersListFilters}
      updateFilters={updateFilters}
      sendGetAdminSettingsRequest={sendGetAdminSettingsRequest}
      sendGetCountryCodeOptionsListForFiltersRequest={sendGetCountryCodeOptionsListForFiltersRequest}
      sendGetRegionOptionsListForFiltersRequest={sendGetRegionOptionsListForFiltersRequest}
      sendGetTeamMembersList={sendGetTeamMembersList}
      countriesOptionsForNumbersSearch={countriesOptionsForNumbersSearch}
      teamMembersList={teamMembersList}
      regionOptionsListForFilters={regionOptionsListForFilters}
      assignPhoneNumberRequestState={assignPhoneNumberRequestState}
      bulkAssignPhoneNumberRequestState={bulkAssignPhoneNumberRequestState}
      setPhoneNumberLabelRequestStatus={setPhoneNumberLabelRequestStatus}
      bulkSetPhoneNumberLabelRequestStatus={bulkSetPhoneNumberLabelRequestStatus}
      deletePhoneNumberRequestStatus={deletePhoneNumberRequestStatus}
      revokePhoneNumberReleaseRequestStatus={revokePhoneNumberReleaseRequestStatus}
    />
  );
};

export default memo(PhoneNumberListContainer);